import { useState } from "react";
import { Bell, BellOff, Loader2, CheckCircle, XCircle, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { toast } from "sonner";

export function NotificationSettings({ token }) {
  const {
    permission,
    isSupported,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe
  } = usePushNotifications(token);
  
  const [testing, setTesting] = useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success("Notificaciones desactivadas");
      } else {
        toast.error("Error al desactivar notificaciones");
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success("¡Notificaciones activadas!");
      } else if (permission === 'denied') {
        toast.error("Permiso denegado. Habilita las notificaciones en la configuración de tu navegador.");
      } else {
        toast.error(error || "Error al activar notificaciones");
      }
    }
  };

  const handleTest = async () => {
    if (!isSubscribed) {
      toast.error("Primero activa las notificaciones");
      return;
    }
    
    setTesting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/push/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Notificación enviada a ${data.sent_count} dispositivo(s)`);
      } else {
        toast.error("Error al enviar notificación de prueba");
      }
    } catch (err) {
      toast.error("Error de conexión");
    } finally {
      setTesting(false);
    }
  };

  if (!isSupported) {
    return (
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-gray-500">
            <BellOff className="h-5 w-5" />
            <div>
              <p className="font-medium">Notificaciones no disponibles</p>
              <p className="text-sm">Tu navegador no soporta notificaciones push</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E2E8F0]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Bell className="h-5 w-5 text-green-600" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                <BellOff className="h-5 w-5 text-gray-400" />
              </div>
            )}
            <div>
              <p className="font-medium text-[#0F172A]">Notificaciones Push</p>
              <p className="text-sm text-[#64748B]">
                {isSubscribed 
                  ? "Recibes alertas de ofertas en subastas, contraofertas y mensajes" 
                  : "Las notificaciones están desactivadas"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isSubscribed && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleTest}
                disabled={testing}
                className="text-xs"
                data-testid="test-notification-btn"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Probar"}
              </Button>
            )}
            <Button
              onClick={handleToggle}
              disabled={isLoading}
              variant={isSubscribed ? "outline" : "default"}
              className={isSubscribed ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-[#475569] hover:bg-[#334155]"}
              data-testid="toggle-notifications-btn"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : isSubscribed ? (
                <BellOff className="h-4 w-4 mr-2" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              {isSubscribed ? "Desactivar" : "Activar"}
            </Button>
          </div>
        </div>
        
        {permission === 'denied' && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Permiso bloqueado</p>
                <p>Ve a la configuración de tu navegador y permite las notificaciones para este sitio.</p>
              </div>
            </div>
          </div>
        )}
        
        {permission === 'default' && !isSubscribed && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p>Haz clic en "Activar" y acepta el permiso del navegador para recibir notificaciones.</p>
              </div>
            </div>
          </div>
        )}
        
        {isSubscribed && (
          <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-800">Notificaciones activas en este dispositivo</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationSettings;
